import { CreateDatasetTableRequest, CreateTableResponse, GetTableListResponse, UpdateDatasetTableRequest, UpdateTableResponse } from 'tabs/data/api/generated'
import { useEffect, useState } from 'react'
import { createDatasetTable, getDatasetTables, updateDatasetTable } from 'tabs/data/api/tables'
import { alertError, alertSuccess } from 'api'

type UseDatasetTablesParams = {
  data: GetTableListResponse['tables']
  loading: boolean
  error: Error | string | null
  createTable: (
    datasetId: string,
    params: CreateDatasetTableRequest['createTableRequest'],
  ) => Promise<CreateTableResponse>
  updateTable: (
    tableId: string,
    datasetId: string,
    force: boolean,
    params: UpdateDatasetTableRequest['updateTableRequest'],
  ) => Promise<UpdateTableResponse>
  refetch: () => Promise<void>
}

export const useDatasetTables = (datasetId: string): UseDatasetTablesParams => {
  const [data, setData] = useState<GetTableListResponse['tables']>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | string | null>(null)

  const createTable = async (
    datasetId: string,
    params: CreateDatasetTableRequest['createTableRequest'],
  ): Promise<CreateTableResponse> => {
    try {
      const response = await createDatasetTable(datasetId, params)
      alertSuccess('Table created', 3000)

      return response
    } catch (error) {
      switch (error.response.status) {
        case 404:
          throw alertError('The table that was already deleted')
        case 409:
          throw alertError('Provided Table name already exists')
        case 412:
          throw alertError('Error! Someone already made changes. Please reload page and try again.')
        default:
          throw alertError(error?.message || error || 'Error creating table')
      }
    }
  }

  const updateTable = async (tableId, datasetId, force, params): Promise<UpdateTableResponse> => {
    try {
      setLoading(true)
      return updateDatasetTable(tableId, datasetId, force, params)
    } catch (error) {
      throw error
    } finally {
      setLoading(false)
    }
  }

  const fetchDatasetVersions = async () => {
    try {
      if (!datasetId) return

      setLoading(true)

      if (loading) return

      setError(null)

      const jsonData = await getDatasetTables(datasetId)
      setData(jsonData?.tables || [])
    } catch (err: unknown) {
      setError((err as Error)?.message || 'An unknown error occurred')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDatasetVersions()
  }, [datasetId])

  return { data, loading, error, refetch: fetchDatasetVersions, createTable, updateTable }
}
